import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { apiGet } from "../services/BaseService";
import { fetchSiteConfig } from "../services/SiteConfigService";
import { fetchSiteStats, SiteStats } from "../services/StatsService";

interface BasicHomeHeroProps {
  title: string;
  subtitle: string;
}

const BasicHomeHero: React.FC<BasicHomeHeroProps> = ({ title, subtitle }) => {
  const [specialVideos, setSpecialVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [siteConfig, setSiteConfig] = useState<any>(null);
  const [stats, setStats] = useState<SiteStats>({ totalStates: 0, totalGroups: 0, totalPrograms: 0, totalSchedules: 0 });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load site configuration
        try {
          const config = await fetchSiteConfig();
          setSiteConfig(config);
        } catch (configError) {
          // Fallback to direct API call
          try {
            const config = await apiGet('/site-config');
            setSiteConfig(config);
          } catch (error) {
            console.error('Failed to fetch site config:', error);
          }
        }
        
        // Load special videos if enabled in config
        try {
          const data = await apiGet('/youtube-videos?isSpecial=true&limit=1');
          setSpecialVideos(data.videos || []);
        } catch (error) {
          console.error('Failed to fetch special videos:', error);
        }
        
        // Load site stats
        const statsData = await fetchSiteStats();
        setStats(statsData);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };
  return (
    <section 
      className="py-5 text-white" 
      style={{ 
        background: "linear-gradient(135deg, #810081 0%, #FFD600 100%)",
        padding: "80px 0"
      }}
    >
      <Container>
        <Row className="align-items-center">
          {/* Featured Video Column */}
          <Col lg={5} className="mb-4 mb-lg-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="light" />
              </div>
            ) : specialVideos.length > 0 ? (
              <Card className="border-0 shadow">
                <div className="ratio ratio-16x9">
                  <iframe
                    src={`https://www.youtube.com/embed/${getYoutubeVideoId(specialVideos[0].videoUrl)}`}
                    title="Featured Video"
                    allowFullScreen
                  ></iframe>
                </div>
                <Card.Body className="bg-dark text-white">
                  <h5>{specialVideos[0].title}</h5>
                  <small className="text">{specialVideos[0].description?.substring(0, 100)}...</small>
                </Card.Body>
              </Card>
            ) : (
              <div className="text-center py-5 bg-dark rounded">
                <i className="bi bi-film display-1"></i>
                <p className="mt-3">Featured video coming soon</p>
              </div>
            )}
          </Col>
          
          {/* Text Column */}
          <Col lg={7} className="text-center text-lg-start">
            <h1 className="display-4 fw-bold mb-3">{title}</h1>
            <p className="lead mb-4">{subtitle}</p>
            
            {/* Site Statistics */}
            <Row className="g-3 mt-4">
              {(() => {
                const visibleBoxes = [
                  (!siteConfig || siteConfig.basicHomeConfig?.showStatesCount !== false),
                  (!siteConfig || siteConfig.basicHomeConfig?.showGroupsCount !== false),
                  (!siteConfig || siteConfig.basicTheaterConfig?.showProgramsCount !== false),
                  (!siteConfig || siteConfig.basicHomeConfig?.showSchedulesCount !== false)
                ].filter(Boolean).length;
                
                const getColSize = () => {
                  if (visibleBoxes === 1) return { xs: 12, md: 6, lg: 4 };
                  if (visibleBoxes === 2) return { xs: 6, md: 6 };
                  if (visibleBoxes === 3) return { xs: 6, md: 4 };
                  return { xs: 6, md: 3 };
                };
                
                const colSize = getColSize();
                
                return (
                  <>
                    {(!siteConfig || siteConfig.basicHomeConfig?.showStatesCount !== false) && (
                      <Col {...colSize}>
                        <Card className="bg-white bg-opacity-90 border-0 text-center h-100">
                          <Card.Body className="py-3">
                            <i className="bi bi-geo-alt-fill display-6 text-warning mb-2"></i>
                            <h3 className="fw-bold text-dark mb-1">{stats.totalStates.toLocaleString()}</h3>
                            <p className="text-muted mb-0 small">Active States</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    
                    {(!siteConfig || siteConfig.basicHomeConfig?.showGroupsCount !== false) && (
                      <Col {...colSize}>
                        <Card className="bg-white bg-opacity-90 border-0 text-center h-100">
                          <Card.Body className="py-3">
                            <i className="bi bi-people-fill display-6 text-warning mb-2"></i>
                            <h3 className="fw-bold text-dark mb-1">{stats.totalGroups.toLocaleString()}</h3>
                            <p className="text-muted mb-0 small">Theater Groups</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    
                    {(!siteConfig || siteConfig.basicTheaterConfig?.showProgramsCount !== false) && (
                      <Col {...colSize}>
                        <Card className="bg-white bg-opacity-90 border-0 text-center h-100">
                          <Card.Body className="py-3">
                            <i className="bi bi-calendar-event-fill display-6 text-warning mb-2"></i>
                            <h3 className="fw-bold text-dark mb-1">{stats.totalPrograms.toLocaleString()}</h3>
                            <p className="text-muted mb-0 small">Plays</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    
                    {(!siteConfig || siteConfig.basicHomeConfig?.showSchedulesCount !== false) && (
                      <Col {...colSize}>
                        <Card className="bg-white bg-opacity-90 border-0 text-center h-100">
                          <Card.Body className="py-3">
                            <i className="bi bi-clock-fill display-6 text-warning mb-2"></i>
                            <h3 className="fw-bold text-dark mb-1">{stats.totalSchedules.toLocaleString()}</h3>
                            <p className="text-muted mb-0 small">Schedules</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                  </>
                );
              })()}
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default BasicHomeHero;